import { Modal, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAdminCompanyController_findAll } from '@api-client/generated/AdminCompanyController/findAll';
import { Schemas } from '@api-client/generated/types';
import { DEFAULT_TIMEOUT_FOR_SEARCH } from '@constants';

import { usePagination } from '../../shared/hooks/usePagination';
import * as S from './styled';

type CompaniesSelectProps = {
  label?: string;
  onChange: (value: string | string[]) => void;
  initialSearchTerm?: string;
  perPage?: number;
  status?: string;
  value?: string | string[];
  mode?: 'multiple' | 'tags';
  placeholder?: string;
  allowClear?: boolean;
  size?: 'large' | 'middle' | 'small';
};

const CompaniesSelect = (props: CompaniesSelectProps) => {
  const [searchTerm, setSearchTerm] = useState(props.initialSearchTerm || '');
  const pagination = usePagination<Schemas.AdminCompanyResponseDto>(searchTerm);

  const { appendData, reset, plainData, metadata, incrementPage, hasNextPage } =
    pagination;

  const { data: apiData, isLoading: apiLoading } =
    useAdminCompanyController_findAll({
      params: {
        page: metadata.currentPage,
        perPage: props.perPage || 20,
        term: searchTerm || undefined,
        status: props.status || undefined,
      },
    });

  const apiOptions = useMemo(() => {
    if (!plainData.length) {
      return [];
    }

    return plainData.map((company: Schemas.AdminCompanyResponseDto) => ({
      label: company.name,
      value: company.id,
    }));
  }, [plainData]);

  const handleDebouncedSearch = useDebouncedCallback((value: string) => {
    setSearchTerm(value);
    reset();
  }, DEFAULT_TIMEOUT_FOR_SEARCH);

  const handleLoadMore = () => {
    incrementPage();
  };

  const handleChange = (value: string | string[]) => {
    // If clearing (value is empty or null) and we had multiple values before
    if (
      (!value || (Array.isArray(value) && value.length === 0)) &&
      props.value &&
      (Array.isArray(props.value) ? props.value.length > 1 : false)
    ) {
      Modal.confirm({
        title: 'Remove all companies?',
        content:
          'Are you sure you want to remove all selected companies at once?',
        okText: 'Yes, remove all',
        cancelText: 'Cancel',
        onOk: () => {
          props.onChange(value);
        },
      });
    } else {
      props.onChange(value);
    }
  };

  useEffect(() => {
    if (apiData) {
      appendData({
        records: apiData.records,
        metadata: {
          ...apiData.metadata,
          currentPage: Number(apiData.metadata.currentPage),
          perPage: Number(apiData.metadata.perPage),
        },
      });
    }
  }, [apiData, appendData]);

  const handleSearch = (value: string) => {
    handleDebouncedSearch(value);
  };

  return (
    <S.Container>
      {props.label && <S.Label>{props.label}</S.Label>}
      <S.StyledSelect>
        <Select
          showSearch
          mode={props.mode}
          value={props.value || []}
          placeholder={props.placeholder || 'Select companies'}
          allowClear={props.allowClear !== undefined ? props.allowClear : true}
          loading={apiLoading}
          options={apiOptions}
          onChange={handleChange}
          onSearch={handleSearch}
          filterOption={false}
          size={props.size || 'large'}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {hasNextPage && (
                <S.LoadMoreContainer>
                  <S.LoadMoreButton
                    onClick={handleLoadMore}
                    disabled={apiLoading}
                  >
                    {apiLoading ? 'Loading...' : 'Load More'}
                  </S.LoadMoreButton>
                </S.LoadMoreContainer>
              )}
            </div>
          )}
        />
      </S.StyledSelect>
    </S.Container>
  );
};

export default CompaniesSelect;
